import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import i18n from '@/i18n'

import locales from './locales'
import type { MutationMeta } from './types'

// Type augmentation for mutation meta
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: MutationMeta
  }
}

export function getContext() {
  // Register the locales
  Object.entries(locales).forEach(([key, value]) => {
    if (!i18n.hasResourceBundle(key, 'tanstack-query')) {
      i18n.addResourceBundle(key, 'tanstack-query', value['tanstack-query'])
    }
  })

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
      },
    },
    mutationCache: new MutationCache({
      onSettled: async (_data, error, _variables, _context, mutation) => {
        if (error) return

        // Invalidate queries if specified
        if (mutation.meta?.invalidatesQuery) {
          const queries = Array.isArray(mutation.meta.invalidatesQuery)
            ? mutation.meta.invalidatesQuery
            : [mutation.meta.invalidatesQuery]

          await Promise.all(
            queries.map((queryKey) =>
              queryClient.invalidateQueries({ queryKey: [queryKey] }),
            ),
          )
        }

        // Show success toast if successMessage is provided
        if (mutation.meta?.successMessage) {
          toast.success(mutation.meta.successMessage as string)
        }
      },
      onError: (_error, _variables, _context, mutation) => {
        // Show error toast
        // If errorMessage is provided, use it
        // If errorMessage is undefined and skipErrorMessage is false (default), show generic error
        if (mutation.meta?.errorMessage) {
          toast.error(mutation.meta.errorMessage as string)
        } else if (!mutation.meta?.skipErrorMessage) {
          const genericError = i18n.t('mutation.genericError', {
            ns: 'tanstack-query',
          })
          toast.error(genericError)
        }
      },
    }),
  })
  return {
    queryClient,
  }
}

export function Provider({
  children,
  queryClient,
}: {
  children: React.ReactNode
  queryClient: QueryClient
}) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
